/**
 * Módulo próprio de redimensionar/trocar imagem no Quill — evitamos depender de libs
 * de terceiros pra isso: as mantidas hoje (ex.: @mgreminger/quill-image-resize-module)
 * são feitas pro Quill 2, e a única compatível com o Quill 1.x deste projeto
 * (quill-image-resize-module original) traz ~445 pacotes transitivos de ferramentas de
 * build de 2017 (webpack 1/babel 6) como dependência direta — desproporcional pro tamanho
 * da feature. Isso aqui só manipula os atributos width/style da própria tag <img>, que o
 * Quill (nativo + ImageComEstilo) já sabe preservar ao salvar/recarregar.
 *
 * Mínimo baixo de propósito — cobre o caso de ícone pequeno (ex.: rede social no rodapé),
 * não só logo. O botão de trocar imagem fica posicionado fora da moldura da imagem
 * (ver CSS de .ql-image-resize-trocar) exatamente pra não impor, ele mesmo, um tamanho
 * mínimo visual maior que esse.
 */
const TAMANHO_MINIMO = 16

export default class ImageResizeModule {
  private quill: any
  private overlay: HTMLDivElement | null = null
  private imagemAtual: HTMLImageElement | null = null

  constructor(quill: any) {
    this.quill = quill

    const container = this.quill.root.parentNode as HTMLElement
    if (getComputedStyle(container).position === 'static') {
      container.style.position = 'relative'
    }

    this.quill.root.addEventListener('click', this.aoClicarNoEditor)
    this.quill.root.addEventListener('scroll', this.aoRolar)
    this.quill.on('text-change', this.aoMudarTexto)
  }

  private aoRolar = () => {
    this.reposicionarOverlay()
  }

  private aoClicarNoEditor = (event: MouseEvent) => {
    const alvo = event.target as HTMLElement

    if (alvo.tagName === 'IMG') {
      this.selecionarImagem(alvo as HTMLImageElement)
    } else if (!this.overlay || !this.overlay.contains(alvo)) {
      this.desselecionar()
    }
  }

  private aoMudarTexto = () => {
    if (this.imagemAtual && !this.quill.root.contains(this.imagemAtual)) {
      this.desselecionar()
    }
  }

  private selecionarImagem(img: HTMLImageElement) {
    this.desselecionar()
    this.imagemAtual = img
    this.criarOverlay()
  }

  private criarOverlay() {
    if (!this.imagemAtual) {
      return
    }

    const container = this.quill.root.parentNode as HTMLElement

    this.overlay = document.createElement('div')
    this.overlay.className = 'ql-image-resize-overlay'
    this.reposicionarOverlay()
    container.appendChild(this.overlay)

    const posicoes = ['nw', 'ne', 'sw', 'se']

    posicoes.forEach((posicao) => {
      const handle = document.createElement('div')
      handle.className = `ql-image-resize-handle ql-image-resize-handle--${posicao}`
      handle.addEventListener('mousedown', event => this.iniciarArraste(event as MouseEvent, posicao))
      this.overlay?.appendChild(handle)
    })

    const botaoTrocar = document.createElement('div')
    botaoTrocar.className = 'ql-image-resize-trocar'
    botaoTrocar.title = 'Trocar imagem'
    botaoTrocar.textContent = '⇄'
    botaoTrocar.addEventListener('mousedown', event => event.stopPropagation())
    botaoTrocar.addEventListener('click', (event) => {
      event.stopPropagation()
      this.trocarImagem()
    })
    this.overlay.appendChild(botaoTrocar)
  }

  private reposicionarOverlay() {
    if (!this.overlay || !this.imagemAtual) {
      return
    }

    const container = this.quill.root.parentNode as HTMLElement
    const rectImg = this.imagemAtual.getBoundingClientRect()
    const rectContainer = container.getBoundingClientRect()

    this.overlay.style.left = `${rectImg.left - rectContainer.left + container.scrollLeft}px`
    this.overlay.style.top = `${rectImg.top - rectContainer.top + container.scrollTop}px`
    this.overlay.style.width = `${rectImg.width}px`
    this.overlay.style.height = `${rectImg.height}px`
  }

  private iniciarArraste(event: MouseEvent, posicao: string) {
    event.preventDefault()
    event.stopPropagation()

    const img = this.imagemAtual

    if (!img) {
      return
    }

    const larguraInicial = img.getBoundingClientRect().width
    const xInicial = event.clientX

    const aoMover = (moveEvent: MouseEvent) => {
      const delta = moveEvent.clientX - xInicial
      const sinal = posicao.includes('w') ? -1 : 1
      const novaLargura = Math.max(TAMANHO_MINIMO, Math.round(larguraInicial + delta * sinal))

      img.setAttribute('width', String(novaLargura))
      img.removeAttribute('height')

      this.reposicionarOverlay()
    }

    const aoSoltar = () => {
      document.removeEventListener('mousemove', aoMover)
      document.removeEventListener('mouseup', aoSoltar)
      this.quill.update()
    }

    document.addEventListener('mousemove', aoMover)
    document.addEventListener('mouseup', aoSoltar)
  }

  private trocarImagem() {
    const img = this.imagemAtual

    if (!img) {
      return
    }

    const input = document.createElement('input')
    input.type = 'file'
    input.accept = 'image/*'
    input.addEventListener('change', () => {
      const arquivo = input.files && input.files[0]

      if (!arquivo) {
        return
      }

      const leitor = new FileReader()
      leitor.onload = () => {
        img.setAttribute('src', String(leitor.result))
        this.quill.update()
        this.reposicionarOverlay()
      }
      leitor.readAsDataURL(arquivo)
    })
    input.click()
  }

  private desselecionar() {
    if (this.overlay) {
      this.overlay.remove()
      this.overlay = null
    }
    this.imagemAtual = null
  }
}
